/**
 * One decision path for every surface that shows a parked send.
 *
 * The chat bubble, the butler card and /assistant/confirm all show the same
 * thing and must do the same thing when a button is pressed: confirm with the
 * session plus the hash the card was given (or the single-use token a host's
 * deep link carried), or cancel. A surface that grew its own version of this
 * would be the second opinion on a safety question this code exists to avoid.
 *
 * Nothing here decides whether the action is allowed — the server's claim is
 * atomic and single-use, and the outcome is whatever it said.
 */
import { AssistantApiError, assistantApi } from '../../services/api/assistantApi';

export type PendingDecision =
  | { readonly outcome: 'sent' }
  | { readonly outcome: 'cancelled' }
  | { readonly outcome: 'expired' }
  | { readonly outcome: 'failed'; readonly message: string };

export interface PendingProof {
  /** The binding the server handed out with the parked action. Not a secret. */
  readonly confirmHash: string;
  /** A host deep link may carry the single-use token; the session proof needs none. */
  readonly token?: string | null;
}

export function isExpired(expiresAt: string, now: number = Date.now()): boolean {
  const at = Date.parse(expiresAt);
  return Number.isFinite(at) && at <= now;
}

function failed(error: unknown): PendingDecision {
  // The server's own words when it has them — "already confirmed or cancelled"
  // is something a person can act on; a generic line is not.
  const message =
    error instanceof AssistantApiError || error instanceof Error ? error.message : '';
  return { outcome: 'failed', message };
}

export async function decidePending(
  actionId: string,
  proof: PendingProof,
  confirm: boolean,
  expiresAt: string,
): Promise<PendingDecision> {
  if (!confirm) {
    try {
      await assistantApi.cancelAction(actionId);
      return { outcome: 'cancelled' };
    } catch (error) {
      return failed(error);
    }
  }
  if (isExpired(expiresAt)) return { outcome: 'expired' };
  try {
    if (proof.token) await assistantApi.confirmAction(actionId, proof.token);
    else await assistantApi.confirmActionBySession(actionId, proof.confirmHash);
    return { outcome: 'sent' };
  } catch (error) {
    return failed(error);
  }
}
